import React from "react";

/**
 * One renderer for a proposal's block document, used by BOTH the builder preview
 * and the public vendor page, so what the agent designs is what the vendor sees.
 *
 * Layout follows the Proply reference: each block is a full-width band with its
 * own background (alternating light/white, a dark band for key figures, a themed
 * call-to-action), and an inner centered container. Because every band is 100%
 * of its parent, the same renderer fills the viewport on the public page and the
 * preview panel in the builder without any viewport-width hacks.
 *
 * Props:
 *   blocks       – [{ type, visible, data }]
 *   theme        – accent colour (hex)
 *   branding     – { logo_url, agency_name } for the hero
 *   comparables  – rows from the linked CMA (for a comparables block with use_cma)
 *   agent        – { name, phone_number, email } fallback for agent cards
 *   acceptSlot   – node rendered inside the call-to-action block
 */
const money = (v) => {
  if (v === "" || v == null) return "";
  const n = Number(String(v).replace(/[$,]/g, ""));
  return Number.isFinite(n) && n ? `$${n.toLocaleString()}` : String(v);
};

// Quote / marketing-investment totals. Mirrors the backend quote_totals() rules
// so the web page and the PDF always agree.
const moneyNum = (v) => { const n = Number(String(v ?? "").replace(/[$,\s]/g, "")); return Number.isFinite(n) ? n : 0; };
const quoteTotals = (d) => {
  const subtotal = (d.items || []).reduce((s, i) => s + moneyNum(i && i.amount), 0);
  const rateNum = Number(String(d.gst_rate ?? 10).trim());
  const rate = (Number.isFinite(rateNum) ? rateNum : 10) / 100;
  let gst, total;
  if (d.gst_inclusive) { gst = rate ? subtotal - subtotal / (1 + rate) : 0; total = subtotal; }
  else { gst = subtotal * rate; total = subtotal + gst; }
  const fmt = (n) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return { subtotal: fmt(subtotal), gst: fmt(gst), total: fmt(total), ratePct: rate * 100, inclusive: !!d.gst_inclusive };
};
const Stars = ({ n }) => {
  const r = Math.max(0, Math.min(5, Math.round(Number(n) || 0)));
  if (!r) return null;
  return <span className="text-[#f5b301] tracking-wider text-sm">{"★".repeat(r)}{"☆".repeat(5 - r)}</span>;
};

// The sending agent's CRM profile photo stands in for an agent card with no
// photo of its own (CG QA, 27 Oct). Matched on email, else name.
const agentPhotoFor = (item, agent) => {
  if (item.photo_url) return item.photo_url;
  if (!agent || !agent.profile_pic_url) return "";
  const same = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  return (item.email && same(item.email, agent.email)) || (!item.email && same(item.name, agent.name))
    ? agent.profile_pic_url : "";
};

// Only http(s) image URLs are drawn (the API strips anything else; this is defence in depth).
const safeImg = (u) => typeof u === "string" && /^https?:\/\//i.test(u.trim());

const LIGHT = "#f5f6f8";
const WHITE = "#ffffff";
const DARK = "#111820";

// data-block-* attrs let the public page's engagement beacon attribute scroll
// time to a section without the renderer knowing about analytics.
const Band = ({ children, bg = WHITE, dark = false, wide = false, blockId, blockType }) => (
  <section style={{ background: bg }} className={dark ? "text-white" : "text-[#1b2740]"} data-block-id={blockId} data-block-type={blockType}>
    <div className={`mx-auto px-6 py-12 md:py-16 ${wide ? "max-w-5xl" : "max-w-4xl"}`}>
      {children}
    </div>
  </section>
);

const Heading = ({ children, theme, dark }) => (
  <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: dark ? "#fff" : theme }}>
    {children}
  </h2>
);

// Turn a pasted media URL into an embeddable source. Validates client-side too
// (the renderer also runs in the builder preview on un-saved input), failing
// closed: https only + host allowlist, mirroring the backend.
const EMBED_HOSTS = [
  "youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be",
  "youtube-nocookie.com", "www.youtube-nocookie.com",
  "vimeo.com", "www.vimeo.com", "player.vimeo.com",
  "loom.com", "www.loom.com",
  "wistia.com", "fast.wistia.net", "fast.wistia.com",
  "matterport.com", "my.matterport.com", "kuula.co", "www.kuula.co",
];
const hostAllowed = (h) => EMBED_HOSTS.includes(h) || h.endsWith(".wistia.net") || h.endsWith(".wistia.com");

const embedInfo = (url) => {
  if (!url) return null;
  let p;
  try { p = new URL(String(url).trim()); } catch (e) { return null; }
  if (p.protocol !== "https:") return null;
  // Direct video file → <video> (media can't execute scripts).
  if (/\.(mp4|webm|mov)$/i.test(p.pathname)) return { type: "video", src: p.href };
  const host = p.hostname.toLowerCase();
  if (!hostAllowed(host)) return null;
  let m;
  if ((m = p.href.match(/(?:youtube\.com\/.*[?&]v=|youtu\.be\/|youtube(?:-nocookie)?\.com\/embed\/)([\w-]{6,})/i)))
    return { type: "iframe", src: `https://www.youtube-nocookie.com/embed/${m[1]}` };
  if ((m = p.href.match(/vimeo\.com\/(?:video\/)?(\d+)/i)))
    return { type: "iframe", src: `https://player.vimeo.com/video/${m[1]}` };
  if ((m = p.href.match(/loom\.com\/(?:share|embed)\/([\w-]+)/i)))
    return { type: "iframe", src: `https://www.loom.com/embed/${m[1]}` };
  return { type: "iframe", src: p.href }; // Matterport / Kuula / Wistia share+embed URLs
};

const osmEmbed = (lat, lng) => {
  const a = Number(lat), o = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(o)) return null;
  const d = 0.01;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${o - d}%2C${a - d}%2C${o + d}%2C${a + d}&layer=mapnik&marker=${a}%2C${o}`;
};

const ProposalBlockRenderer = ({
  blocks = [], theme = "#1f2a44", branding = null, comparables = [], agent = null, acceptSlot = null,
}) => {
  const visible = (blocks || []).filter((b) => b && b.visible !== false);
  // Alternate the background of ordinary content bands (light / white), like Proply.
  let tone = 0;
  const altBg = () => (tone++ % 2 === 0 ? WHITE : LIGHT);

  return (
    <div className="proposal-doc">
      {visible.map((block, i) => {
        const d = block.data || {};
        const key = block.id || `${block.type}-${i}`;

        switch (block.type) {
          case "cover": {
            const addr = d.address || "";
            if (d.layout === "panel") {
              // Market-update letter: logo on a white panel, address over the photo.
              return (
                <section key={key} className="relative" data-block-id={key} data-block-type={block.type}>
                  <div
                    className="relative w-full min-h-[420px] md:min-h-[540px] bg-cover bg-center flex flex-col"
                    style={d.image_url
                      ? { backgroundImage: `url(${d.image_url})` }
                      : { background: `linear-gradient(135deg, ${theme}, #0b1220)` }}
                  >
                    <div className="absolute inset-0 bg-black/25" />
                    <div className="relative bg-white w-full sm:w-[56%] md:w-[44%] mt-10 md:mt-14 px-8 py-8 flex items-center justify-center">
                      {branding?.logo_url
                        ? <img src={branding.logo_url} alt={branding.agency_name || ""} className="h-12 md:h-14 object-contain" />
                        : <span className="font-semibold tracking-[0.3em] text-lg" style={{ color: theme }}>{(branding?.agency_name || "").toUpperCase()}</span>}
                    </div>
                    <div className="relative flex-1 flex items-center justify-center px-6 py-12 text-center">
                      <div>
                        <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,.45)]">
                          {d.headline || addr || "Your property"}
                        </h1>
                        {d.subheadline && <p className="mt-3 text-lg md:text-2xl text-white/90 drop-shadow">{d.subheadline}</p>}
                      </div>
                    </div>
                  </div>
                </section>
              );
            }
            return (
              <section key={key} className="relative" data-block-id={key} data-block-type={block.type}>
                <div
                  className="relative w-full min-h-[440px] md:min-h-[560px] bg-cover bg-center"
                  style={d.image_url
                    ? { backgroundImage: `url(${d.image_url})` }
                    : { background: `linear-gradient(135deg, ${theme}, #0b1220)` }}
                >
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,.72), rgba(0,0,0,.15) 55%, rgba(0,0,0,.35))" }} />
                  <div className="relative mx-auto max-w-5xl px-6 min-h-[440px] md:min-h-[560px] flex flex-col">
                    {(branding?.logo_url || branding?.agency_name) && (
                      <div className="pt-8">
                        {branding.logo_url
                          ? <img src={branding.logo_url} alt={branding.agency_name || ""} className="h-12 object-contain" />
                          : <span className="inline-block border-2 border-white/90 text-white font-semibold px-4 py-2 rounded">{branding.agency_name}</span>}
                      </div>
                    )}
                    <div className="mt-auto pb-10 text-white">
                      {d.badge && (
                        <span className="inline-block text-xs font-bold tracking-wider px-3 py-1.5 rounded mb-4" style={{ background: theme }}>
                          {d.badge}
                        </span>
                      )}
                      <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
                        {d.headline || "Your property proposal"}
                      </h1>
                      {(d.subheadline || addr) && (
                        <p className="mt-2 text-lg md:text-2xl text-white/90 uppercase tracking-wide">
                          {addr || d.subheadline}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            );
          }

          case "about_agent":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>About your agent</Heading>
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  {safeImg(agent?.profile_pic_url) && (
                    <img src={agent.profile_pic_url} alt={agent.name || ""}
                      className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover shrink-0 border-4 border-white shadow" />
                  )}
                  <div>
                    <p className="whitespace-pre-line leading-relaxed text-lg">{d.text}</p>
                    {agent && (
                      <p className="mt-3 text-sm text-gray-500">
                        {[agent.name, agent.phone_number, agent.email].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                </div>
              </Band>
            );

          case "text":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                {d.heading && <Heading theme={theme}>{d.heading}</Heading>}
                <p className="whitespace-pre-line leading-relaxed text-lg text-gray-700">{d.body}</p>
              </Band>
            );

          case "estimate":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <div className="flex flex-wrap items-baseline gap-x-4">
                  {(d.sale_min || d.sale_max) && (
                    <div className="text-4xl md:text-5xl font-extrabold" style={{ color: theme }}>
                      {money(d.sale_min)}{d.sale_min && d.sale_max ? " – " : ""}{money(d.sale_max)}
                    </div>
                  )}
                </div>
                {d.comment && <p className="mt-3 text-lg text-gray-600 whitespace-pre-line">{d.comment}</p>}
              </Band>
            );

          case "comparables":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>Comparable sales</Heading>
                {d.intro && <p className="text-gray-600 mb-4 text-lg">{d.intro}</p>}
                {(d.items || []).filter((c) => c && c.address).length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    {d.items.filter((c) => c && c.address).map((c, j) => (
                      <div key={j} className="rounded-lg overflow-hidden border border-gray-100 bg-white">
                        {safeImg(c.image_url)
                          ? <img src={c.image_url} alt="" className="w-full h-36 object-cover" />
                          : <div className="w-full h-36" style={{ background: `${theme}22` }} />}
                        <div className="p-3">
                          <p className="font-semibold text-gray-900 leading-snug">{c.address}{c.suburb ? `, ${c.suburb}` : ""}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {[c.beds && `${c.beds} bed`, c.baths && `${c.baths} bath`, c.cars && `${c.cars} car`].filter(Boolean).join(" · ")}
                          </p>
                          <div className="flex items-baseline justify-between mt-2">
                            <span className="text-lg font-bold" style={{ color: theme }}>{c.price_label || ""}</span>
                            {c.sold_date && <span className="text-xs text-gray-400">Sold {c.sold_date}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {d.use_cma && comparables.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="text-left text-gray-400 uppercase text-[11px]">
                          <th className="py-2 pr-3">Address</th><th className="py-2 px-2">Beds</th>
                          <th className="py-2 px-2">Baths</th><th className="py-2 px-2">Cars</th>
                          <th className="py-2 px-2">Price</th><th className="py-2 px-2">Sold</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparables.map((c) => (
                          <tr key={c.id} className="border-t border-gray-100">
                            <td className="py-2 pr-3">{c.full_address}</td>
                            <td className="py-2 px-2">{c.bedrooms ?? "–"}</td>
                            <td className="py-2 px-2">{c.bathrooms ?? "–"}</td>
                            <td className="py-2 px-2">{c.car_spaces ?? "–"}</td>
                            <td className="py-2 px-2">{c.price_label || (c.price ? money(c.price) : "–")}</td>
                            <td className="py-2 px-2">{c.sold_date || "–"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Band>
            );

          case "gallery": {
            const imgs = (d.images || []).filter((im) => im && im.image_url);
            if (!imgs.length && !d.heading) return null;
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()} wide>
                {d.heading && <Heading theme={theme}>{d.heading}</Heading>}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {imgs.map((im, j) => (
                    <figure key={j} className="overflow-hidden rounded-lg bg-gray-100">
                      <img src={im.image_url} alt={im.caption || ""} className="w-full h-48 object-cover" />
                      {im.caption && <figcaption className="text-xs text-gray-500 px-2 py-1">{im.caption}</figcaption>}
                    </figure>
                  ))}
                </div>
              </Band>
            );
          }

          case "feature_list":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                {d.heading && <Heading theme={theme}>{d.heading}</Heading>}
                {d.intro && <p className="text-gray-600 mb-4 text-lg">{d.intro}</p>}
                <ul className="space-y-2">
                  {(d.items || []).filter((it) => it && it.text).map((item, j) => (
                    <li key={j} className="flex gap-3 text-lg text-gray-700">
                      <span style={{ color: theme }} className="font-bold">—</span>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </Band>
            );

          case "metrics": {
            const dark = !!d.dark;
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={dark ? DARK : altBg()} dark={dark}>
                {d.heading && <Heading theme={theme} dark={dark}>{d.heading}</Heading>}
                <div className="flex flex-wrap gap-x-16 gap-y-8">
                  {(d.items || []).map((item, j) => (
                    <div key={j}>
                      <div className={`text-sm ${dark ? "text-white/70" : "text-gray-500"}`}>{item.label}</div>
                      <div className="text-3xl md:text-4xl font-extrabold" style={{ color: dark ? "#fff" : theme }}>
                        {item.value || "—"}
                      </div>
                    </div>
                  ))}
                </div>
                {d.footnote && <p className={`mt-6 text-sm ${dark ? "text-white/60" : "text-gray-500"}`}>{d.footnote}</p>}
              </Band>
            );
          }

          case "market_stats":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                {d.heading && <Heading theme={theme}>{d.heading}</Heading>}
                {d.intro && <p className="text-gray-600 mb-5 text-lg max-w-2xl">{d.intro}</p>}
                <table className="w-full border-collapse">
                  <tbody>
                    {(d.items || []).map((item, j) => {
                      const trendColor = item.trend === "up" ? "#188a4c" : item.trend === "down" ? "#c0392b" : theme;
                      return (
                        <tr key={j}>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">{item.label}</td>
                          <td className="border border-gray-200 px-4 py-3 text-right font-bold" style={{ color: trendColor }}>
                            {item.value || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Band>
            );

          case "map": {
            const embed = osmEmbed(d.lat, d.lng);
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()} wide>
                {d.heading && <Heading theme={theme}>{d.heading}</Heading>}
                {d.address && <p className="text-gray-600 mb-4 text-lg">{d.address}</p>}
                {embed ? (
                  <iframe
                    title="Location map" src={embed} loading="lazy"
                    className="w-full h-80 rounded-xl border border-gray-200"
                  />
                ) : d.address ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.address)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-block font-semibold underline" style={{ color: theme }}
                  >
                    View on map
                  </a>
                ) : null}
              </Band>
            );
          }

          case "embed": {
            const info = embedInfo(d.embed_url);
            if (!info && !d.heading) return null;
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()} wide>
                {d.heading && <Heading theme={theme}>{d.heading}</Heading>}
                {d.caption && <p className="text-gray-600 mb-4 text-lg">{d.caption}</p>}
                {info && info.type === "video" ? (
                  <video src={info.src} controls playsInline preload="metadata"
                    poster={d.poster_url || undefined}
                    className="w-full rounded-xl border border-gray-200 bg-black" />
                ) : info ? (
                  <div className="relative w-full rounded-xl overflow-hidden border border-gray-200" style={{ paddingTop: "56.25%" }}>
                    <iframe
                      title={d.heading || "Embedded media"} src={info.src} loading="lazy"
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen; xr-spatial-tracking"
                      referrerPolicy="no-referrer"
                      sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                      allowFullScreen
                    />
                  </div>
                ) : null}
              </Band>
            );
          }

          case "marketing_plan":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>Marketing plan</Heading>
                {d.intro && <p className="text-gray-600 mb-4 text-lg">{d.intro}</p>}
                <div className="space-y-2">
                  {(d.items || []).map((item, j) => (
                    <div key={j} className="py-2 border-b border-gray-100 last:border-0">
                      <span className="font-semibold">{item.title}</span>
                      {item.text && <div className="text-gray-600">{item.text}</div>}
                    </div>
                  ))}
                </div>
              </Band>
            );

          case "fees":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>Fees</Heading>
                {d.intro && <p className="text-gray-600 mb-2 text-lg">{d.intro}</p>}
                {d.commission_text && <p className="mb-2">{d.commission_text}</p>}
                <div className="space-y-2">
                  {(d.items || []).map((item, j) => (
                    <div key={j} className="py-2 border-b border-gray-100 last:border-0">
                      <span className="font-semibold">{item.label}</span> {item.amount_text}
                      {item.note && <div className="text-gray-500 text-sm">{item.note}</div>}
                    </div>
                  ))}
                </div>
              </Band>
            );

          case "testimonials":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>{d.heading || "What our clients say"}</Heading>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {(d.items || []).map((item, j) => (
                    <blockquote key={j} className="rounded-xl border border-gray-100 bg-white p-5 text-gray-700">
                      <Stars n={item.rating} />
                      <p className="mt-2 italic">“{item.quote}”</p>
                      <span className="block not-italic text-gray-400 text-sm mt-2">
                        — {item.name}{item.suburb ? `, ${item.suburb}` : ""}
                      </span>
                    </blockquote>
                  ))}
                </div>
              </Band>
            );

          case "quote": {
            const t = quoteTotals(d);
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>{d.heading || "Marketing investment"}</Heading>
                {d.intro && <p className="text-gray-600 mb-4 text-lg">{d.intro}</p>}
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-left text-gray-400 uppercase text-[11px]">
                      <th className="py-2">Items</th><th className="py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.items || []).map((item, j) => (
                      <tr key={j} className="border-t border-gray-100">
                        <td className="py-2.5 text-gray-700">{item.label}</td>
                        <td className="py-2.5 text-right font-semibold">{item.amount ? `$${moneyNum(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-200"><td className="py-2.5 text-gray-500">Subtotal</td><td className="py-2.5 text-right">{t.subtotal}</td></tr>
                    <tr><td className="py-1 text-gray-500">GST ({t.ratePct}%){t.inclusive ? " included" : ""}</td><td className="py-1 text-right">{t.gst}</td></tr>
                    <tr className="border-t border-gray-200"><td className="py-3 font-bold text-gray-900">Total incl. GST</td><td className="py-3 text-right text-xl font-extrabold" style={{ color: theme }}>{t.total}</td></tr>
                  </tbody>
                </table>
                {d.note && <p className="mt-3 text-sm text-gray-500">{d.note}</p>}
              </Band>
            );
          }

          case "sale_method":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>{d.heading || "Sale method & pricing"}</Heading>
                {d.method && (
                  <div className="mb-4">
                    <div className="text-sm text-gray-500">Method of sale</div>
                    <div className="inline-block mt-1 px-3 py-1.5 rounded-lg text-white font-bold" style={{ background: theme }}>{d.method}</div>
                  </div>
                )}
                {d.intro && <p className="text-gray-700 text-lg mb-3">{d.intro}</p>}
                {(d.reasons || []).filter((r) => r && r.text).length > 0 && (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                    {d.reasons.filter((r) => r && r.text).map((r, j) => (
                      <li key={j} className="flex gap-2 text-gray-700"><span className="font-bold" style={{ color: theme }}>✓</span>{r.text}</li>
                    ))}
                  </ul>
                )}
                {d.campaign_length && <p className="text-gray-700"><span className="font-semibold">Campaign length:</span> {d.campaign_length}</p>}
                {(d.price_min || d.price_max) && (
                  <div className="mt-4">
                    <div className="text-sm text-gray-500">Advertised range</div>
                    <div className="text-3xl font-extrabold" style={{ color: theme }}>
                      {money(d.price_min)}{d.price_min && d.price_max ? " – " : ""}{money(d.price_max)}
                    </div>
                  </div>
                )}
                {d.price_note && <p className="mt-2 text-sm text-gray-500">{d.price_note}</p>}
              </Band>
            );

          case "steps":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>{d.heading || "Next steps"}</Heading>
                {d.intro && <p className="text-gray-600 mb-5 text-lg">{d.intro}</p>}
                <ol className="space-y-4">
                  {(d.items || []).filter((i) => i && (i.title || i.text)).map((item, j) => (
                    <li key={j} className="flex gap-4">
                      <span className="shrink-0 w-9 h-9 rounded-full text-white font-bold flex items-center justify-center" style={{ background: theme }}>{j + 1}</span>
                      <div>
                        {item.title && <div className="font-bold text-gray-900">{item.title}</div>}
                        {item.text && <div className="text-gray-600">{item.text}</div>}
                      </div>
                    </li>
                  ))}
                </ol>
              </Band>
            );

          case "listing_details":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>Listing details</Heading>
                {d.intro && <p className="text-gray-600 mb-4 text-lg">{d.intro}</p>}
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-md">
                  {(d.items || []).map((item, j) => (
                    <React.Fragment key={j}>
                      <span className="text-gray-500">{item.label}</span>
                      <span className="font-semibold text-right">{item.value}</span>
                    </React.Fragment>
                  ))}
                </div>
              </Band>
            );

          case "open_home_times":
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()}>
                <Heading theme={theme}>Open home times</Heading>
                {d.intro && <p className="text-gray-600 mb-4 text-lg">{d.intro}</p>}
                <div className="space-y-2">
                  {(d.items || []).map((item, j) => (
                    <div key={j} className="py-2 border-b border-gray-100 last:border-0">
                      <span className="font-semibold">{item.when}</span>
                      {item.note && <div className="text-gray-600">{item.note}</div>}
                    </div>
                  ))}
                </div>
              </Band>
            );

          case "agents": {
            const items = (d.items || []).filter((it) => it && (it.name || it.photo_url));
            if (!items.length) return null;
            return (
              <Band key={key} blockId={key} blockType={block.type} bg={altBg()} wide>
                {d.heading && <Heading theme={theme}>{d.heading}</Heading>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {items.map((item, j) => {
                    const photo = agentPhotoFor(item, agent);
                    return (
                    <div key={j} className="relative rounded-2xl overflow-hidden min-h-[340px] bg-gray-200">
                      {safeImg(photo)
                        ? <img src={photo} alt={item.name || ""} className="absolute inset-0 w-full h-full object-cover" />
                        : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${theme}, #0b1220)` }} />}
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,.7), rgba(0,0,0,0) 55%)" }} />
                      <div className="absolute bottom-0 p-5 text-white">
                        <div className="text-lg font-bold uppercase tracking-wide">{item.name}</div>
                        {item.role && <div className="text-white/80 text-sm uppercase">{item.role}</div>}
                        {(item.phone || item.email) && (
                          <div className="text-white/70 text-xs mt-1">
                            {[item.phone, item.email].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </Band>
            );
          }

          case "cta":
            return (
              <section key={key} style={{ background: theme }} className="text-white" data-block-id={key} data-block-type={block.type}>
                <div className="mx-auto max-w-4xl px-6 py-14 md:py-20">
                  <h2 className="text-3xl md:text-5xl font-extrabold">{d.heading || "Ready to move forward?"}</h2>
                  {d.text && <p className="mt-2 text-lg text-white/90 max-w-2xl">{d.text}</p>}
                  {acceptSlot}
                </div>
              </section>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

export default ProposalBlockRenderer;
